import { GetItemsForWeightSetResponse } from '@upright/common/model/output/scores/model-entity-data/api-types';
import { GetWeightSetResultItemDataInternalResponse } from '@upright/common/model/output/weight-sets/api-types';
import { safeAssert } from '@upright/common/safe-assert';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { fetchApiWithError } from '@upright/ui/api/common/fetchers/fetch-with-error';
import { optionsToString } from '@upright/ui/api/common/util';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GetWeightSetItemDataRequest,
  GetWeightSetItemDataResponse,
} from '@upright/common/model/output/scores/model-entity-data/api-types';
import { ApiResponse } from '../../../common/types';

// Used in import view only
export async function fetchWeightSetItemData(
  payload: GetWeightSetItemDataRequest,
  version: string,
): Promise<ApiResponse<GetWeightSetItemDataResponse>> {
  const url = `/scores/${version}/weight-set-items`;
  return await fetchApiWithError<GetWeightSetItemDataResponse>(url, 'POST', payload);
}

async function getWeightSetItems(
  weightSetId: number,
  release: string,
  options: { includeEuTaxonomyMetrics: boolean; includePaiMetrics: boolean },
) {
  return await fetchOrThrow<GetItemsForWeightSetResponse>(
    `/scores/${release}/weight-set-items/${weightSetId}${optionsToString(options)}`,
  );
}

export const getWeightSetItemsQueryKey = 'getWeightSetItemResults';

export function useWeightSetItemResults(
  weightSetId: number | undefined,
  release: string | null,
  options: { includeEuTaxonomyMetrics: boolean; includePaiMetrics: boolean },
) {
  return useQuery({
    queryKey: [
      getWeightSetItemsQueryKey,
      weightSetId,
      release,
      { includeEuTaxonomyMetrics: options.includeEuTaxonomyMetrics, includePaiMetrics: options.includePaiMetrics },
    ],

    queryFn: async () => {
      safeAssert(weightSetId !== undefined);
      safeAssert(release !== null);
      return await getWeightSetItems(weightSetId, release, options);
    },

    enabled: weightSetId !== undefined && release !== null,
  });
}

// Currently invalidates all weight set item queries. Could alternatively load the list of weight sets ids and only invalidate queries of those. -ML
export function useInvalidateGetWeightSetItemDataForAllWeightsetsQuery() {
  const queryClient = useQueryClient();
  return {
    invalidate: async () => {
      await queryClient.invalidateQueries({
        queryKey: [getWeightSetItemsQueryKey],
      });
    },
  };
}

export async function fetchWeightSetResultItemDataInternal(
  weightSetResultId: number,
): Promise<GetWeightSetResultItemDataInternalResponse> {
  return await fetchOrThrow<GetWeightSetResultItemDataInternalResponse>(
    `/results/weight-set-result-item-data-internal/${weightSetResultId}`,
  );
}
